import { REQUIRED_ASSET_PATHS, AUDIO_PATHS, CONFIG } from './config';
import type { NarrationLine } from './types';
import {
  type VoiceManifest,
  EMPTY_VOICE_MANIFEST,
  parseVoiceManifest,
} from './voices';

/**
 * LOAD -> READY -> STARTED boot gate (PRP AC-01 + amendment 1).
 *
 * The app is NOT declared ready merely because index.html loaded. The gate
 * waits for every required art/audio/config response to succeed AND (where
 * service workers are supported) for the SW precache to actually contain the
 * sentinel runtime assets before the start button is shown. If SW is
 * unsupported, the documented online-only fallback applies.
 *
 * Narration lines parsed during this gate are cached so the start-gesture
 * handler can construct a Narration synchronously and call play('intro')
 * inside the user-gesture tick (PRP AC-02, iOS audio unlock).
 */

export interface BootGateHandles {
  loadingOverlay: HTMLElement;
  retryButton: HTMLButtonElement;
  startScreen: HTMLElement;
  startButton: HTMLButtonElement;
}

export interface BootGateCallbacks {
  onReady: () => void;
  onStart: () => void;
}

/** Assets whose presence in the precache proves offline-ready install. */
const PRECACHE_SENTINELS = [
  '/source/narration.json',
  '/audio/intro.mp3',
  '/art/earth.svg',
] as const;

const PRECACHE_CONFIRM_TIMEOUT_MS = 8000;
const PRECACHE_POLL_INTERVAL_MS = 250;

let cachedNarrationLines: NarrationLine[] | null = null;
let cachedVoiceManifest: VoiceManifest = EMPTY_VOICE_MANIFEST;

/**
 * Returns the narration lines parsed during the boot gate. Throws if the gate
 * has not yet completed — callers (the start handler) run only after onReady.
 */
export function getCachedNarrationLines(): NarrationLine[] {
  if (!cachedNarrationLines) {
    throw new Error('narration lines not loaded — boot gate did not complete');
  }
  return cachedNarrationLines;
}

/**
 * Returns the voice manifest cached during the boot gate (returns EMPTY if none; never throws).
 */
export function getCachedVoiceManifest(): VoiceManifest {
  return cachedVoiceManifest ?? EMPTY_VOICE_MANIFEST;
}

export function initBootGate(handles: BootGateHandles, cb: BootGateCallbacks) {
  let attemptToken = 0;
  handles.startButton.addEventListener('click', () => cb.onStart());
  void attempt(attemptToken);

  async function attempt(token: number) {
    handles.retryButton.hidden = true;
    try {
      await fetchAllRequired();
      await confirmPrecacheSentinels();
      if (token !== attemptToken) return;
      cb.onReady();
    } catch (err) {
      console.warn('boot gate failed', err);
      if (token !== attemptToken) return;
      // Non-blocking retry state: overlay stays, retry button revealed.
      handles.retryButton.hidden = false;
    }
  }

  handles.retryButton.addEventListener('click', () => {
    attemptToken += 1;
    void attempt(attemptToken);
  });
}

async function fetchAllRequired(): Promise<void> {
  // Manifest + narration drive the asset list; fetch each path and require 200.
  const manifestRes = await fetch('/source/asset-manifest.json', { cache: 'force-cache' });
  if (!manifestRes.ok) throw new Error(`asset-manifest ${manifestRes.status}`);
  const manifest = (await manifestRes.json()) as Array<{ path: string }>;

  const narrationRes = await fetch('/source/narration.json', { cache: 'force-cache' });
  if (!narrationRes.ok) throw new Error(`narration.json ${narrationRes.status}`);
  const narration = (await narrationRes.json()) as NarrationLine[];

  const [narrationCountRes, narrationFuelRes] = await Promise.all([
    fetch('/source/narration-count.json', { cache: 'force-cache' }),
    fetch('/source/narration-fuel.json', { cache: 'force-cache' }),
  ]);
  if (!narrationCountRes.ok) throw new Error(`narration-count.json ${narrationCountRes.status}`);
  const countRaw = await narrationCountRes.json();
  const narrationCount = Array.isArray(countRaw) ? (countRaw as NarrationLine[]) : [];

  if (!narrationFuelRes.ok) throw new Error(`narration-fuel.json ${narrationFuelRes.status}`);
  const fuelRaw = await narrationFuelRes.json();
  const narrationFuel = Array.isArray(fuelRaw) ? (fuelRaw as NarrationLine[]) : [];

  cachedNarrationLines = [...narration, ...narrationCount, ...narrationFuel];

  let rawVoices: unknown = null;
  try {
    const voicesRes = await fetch('/source/voices.json', { cache: 'force-cache' });
    if (voicesRes.ok) {
      rawVoices = await voicesRes.json();
    }
  } catch {
    /* voices.json failure -> EMPTY manifest */
  }
  const voiceManifest = parseVoiceManifest(rawVoices);

  // Child voice files implied by the manifest are fetched TOLERANTLY:
  // a manifest-listed child file that 404s must NOT fail the boot gate;
  // instead drop it from the cached manifest so the runtime never selects a missing file.
  const validZoe = (
    await Promise.all(
      voiceManifest.zoe.map(async (id) => {
        try {
          const res = await fetch(`/audio/zoe/${id}.mp3`, { cache: 'force-cache' });
          return res.ok ? id : null;
        } catch {
          return null;
        }
      }),
    )
  ).filter((id): id is string => id !== null);

  const validMax = (
    await Promise.all(
      voiceManifest.max.map(async (id) => {
        try {
          const res = await fetch(`/audio/max/${id}.mp3`, { cache: 'force-cache' });
          return res.ok ? id : null;
        } catch {
          return null;
        }
      }),
    )
  ).filter((id): id is string => id !== null);

  cachedVoiceManifest = { zoe: validZoe, max: validMax };

  const paths = new Set<string>(REQUIRED_ASSET_PATHS);
  for (const entry of manifest) paths.add(entry.path);
  for (const line of cachedNarrationLines) paths.add(line.audioPath);
  for (const audio of AUDIO_PATHS) paths.add(audio);

  await Promise.all(
    [...paths].map(async (p) => {
      const res = await fetch(p, { cache: 'force-cache' });
      if (!res.ok) throw new Error(`${p} ${res.status}`);
    }),
  );
}

/**
 * Same-origin confirmation that the active service worker has populated a
 * precache cache containing every sentinel runtime asset. Polls until found or
 * until the timeout elapses, then rejects so the gate surfaces a retry. If
 * service workers / CacheStorage are unsupported, resolves immediately
 * (documented online-only fallback per PRP amendment 1).
 */
async function confirmPrecacheSentinels(): Promise<void> {
  if (!('serviceWorker' in navigator) || typeof caches === 'undefined') return;
  if (!(await navigator.serviceWorker.getRegistration?.())) return;
  const deadline = Date.now() + PRECACHE_CONFIRM_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (await precacheHasSentinels()) return;
    await new Promise((r) => setTimeout(r, PRECACHE_POLL_INTERVAL_MS));
  }
  throw new Error('precache sentinels not present before timeout');
}

async function precacheHasSentinels(): Promise<boolean> {
  try {
    const keys = await caches.keys();
    const precaches = keys.filter((n) => n.includes('precache'));
    if (!precaches.length) return false;
    for (const name of precaches) {
      const cache = await caches.open(name);
      const reqs = await cache.keys();
      // Workbox keys URLs with a __WB_REVISION__ query — compare pathnames.
      const pathNames = new Set(reqs.map((r) => new URL(r.url).pathname));
      if (PRECACHE_SENTINELS.every((s) => pathNames.has(s))) return true;
    }
  } catch {
    /* CacheStorage access can throw in opaque contexts; treat as not-yet. */
  }
  return false;
}

export { CONFIG };
